import { useEffect, useState } from "react";
import { adminApi, type AdminSubstitutionDetail } from "@/services/api/admin";
import {
  teacherApi,
  type IncomingRequest,
  type PrepSummary,
  type RequestLesson,
} from "@/services/api/teacher";
import {
  teacherPriorityApi,
  type PriorityRequestListItem,
} from "@/services/api/teacherPriority";

async function loadOwnRequest(
  requestId: string,
  signal: AbortSignal
): Promise<PriorityRequestListItem | null> {
  if (signal.aborted || !teacherPriorityApi.listRequests) return null;
  const list = await teacherPriorityApi.listRequests(
    undefined,
    0,
    1,
    undefined,
    undefined,
    requestId
  );
  if (signal.aborted) return null;
  return list.requests.find((item) => item.id === requestId) ?? null;
}

export function useTalkRadarDetailLoad(
  role: "admin" | "teacher",
  requestId: string | null,
  kind: "incoming" | "own" = "incoming"
) {
  const [admin, setAdmin] = useState<AdminSubstitutionDetail | null>(null);
  const [teacher, setTeacher] = useState<
    (IncomingRequest & { notes?: string | null }) | null
  >(null);
  const [teacherLessons, setTeacherLessons] = useState<RequestLesson[]>([]);
  const [ownPrep, setOwnPrep] = useState<PrepSummary | null>(null);
  const [ownRequest, setOwnRequest] = useState<PriorityRequestListItem | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!requestId) return;
    const ac = new AbortController();
    let live = true;
    setError(null);
    setAdmin(null);
    setTeacher(null);
    setTeacherLessons([]);
    setOwnPrep(null);
    setOwnRequest(null);
    const load =
      role === "admin"
        ? adminApi.getSubstitutionDetail(requestId).then((data) => {
            if (live) setAdmin(data);
          })
        : kind === "own"
          ? Promise.all([
              teacherApi.getRequestLessons(requestId),
              teacherApi.getRequestDetails(requestId).catch(() => null),
            ]).then(([lessons, details]) => {
              if (!live) return;
              setTeacherLessons(lessons.lessons || []);
              setOwnPrep(details?.prep_summary ?? null);
            }).then(() => {
              if (!live) return;
              return loadOwnRequest(requestId, ac.signal)
                .then((item) => {
                  if (live) setOwnRequest(item);
                })
                .catch(() => {
                  if (live) setOwnRequest(null);
                });
            })
          : Promise.all([
              teacherApi.getIncomingRequestDetail(requestId),
              teacherApi.getRequestLessons(requestId),
            ]).then(([card, lessons]) => {
              if (!live) return;
              setTeacher(card);
              setTeacherLessons(lessons.lessons || []);
            });
    load.catch(() => {
      if (live) setError("Anfrage konnte nicht geladen werden");
    });
    return () => {
      live = false;
      ac.abort();
    };
  }, [role, requestId, kind]);

  return {
    admin,
    setAdmin,
    teacher,
    teacherLessons,
    setTeacherLessons,
    ownPrep,
    ownRequest,
    error,
  };
}
